<template>
  <v-container>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      :position="mobile ? 'bottom' : 'top'"
    >
      {{ snackbarText }}

      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <div class="d-flex align-center mb-4">
      <h1 class="text-h3">Late Shipments</h1>
    </div>

    <shipment-search-form
        v-if="!loading && !error"
        :initial-values="searchParams"
        @search="handleSearch"
      />
    <!-- Loading State -->
    <v-card v-if="loading">
      <v-card-text>
        <div class="d-flex align-center justify-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <v-alert
      v-else-if="error"
      type="error"
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <!-- Content -->
    <template v-else>

      <v-card class="mb-4">
            <v-card-title class="d-flex align-center">
              <v-checkbox
                v-model="selectAll"
                @update:model-value="toggleSelectAll"
                label="Select All"
                hide-details
                density="compact"
                class="mr-4"
              ></v-checkbox>
              
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                :disabled="selectedSales.length === 0"
                class="mr-4"
                @click="handleSubmitTrackingRequest"
              >
                Submit Tracking Request ({{ selectedSales.length }})
              </v-btn>
              <v-btn
                color="error"
                :disabled="selectedSales.length === 0"
                :loading="ignoring"
                class="mr-4"
                prepend-icon="mdi-delete"
                @click="ignoreSales"
              >
                Ignore ({{ selectedSales.length }})
              </v-btn>
              <v-btn
                color="secondary"
                prepend-icon="mdi-file-import"
                @click="importTracking"
              >
                Import tracking
              </v-btn>
            </v-card-title>
          </v-card>
          <v-card class="mb-4">
            <v-card-text class="d-flex align-center">
              <div class="h6 mb-4">Showing {{ sales.length }} of {{ totalItems }} late shipments</div>
              <v-spacer></v-spacer>
              <pagination-links
                :current-page="currentPage"
                :total-pages="totalPages"
                @page-change="handlePageChange"
              />
            </v-card-text>
          </v-card>
      <late-shipment-item
        v-for="sale in sales"
        :key="sale.ID"
        :sale="sale"
        :selected="selectedSales.includes(sale.ID)"
        @selection-changed="handleSelectionChanged"
        @update:sale="handleSaleUpdate"
        @send-message="openMessageDialog(sale)"
      />

      <!-- Pagination -->
      <div class="d-flex justify-center mt-4">
        <pagination-links
          v-if="totalPages > 1"
          :current-page="currentPage"
          :total-pages="totalPages"
          @page-change="handlePageChange"
        />
      </div>
    </template>

    <!-- Message Dialog -->
    <v-dialog v-model="showMessageDialog" max-width="700px">
      <v-card>
        <v-card-title>Send Message</v-card-title>
        <v-card-text>
          <send-email-form
            :to="to"
            :subject="subject"
            :message="message"
            :loading="sending"
            @submit="handleSendMessage"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

      <!-- Import Dialog -->
      <v-dialog v-model="showImportDialog" max-width="600px">
        <v-card>
          <v-card-title>Import Tracking Numbers</v-card-title>
          <v-card-text>
            <p class="mb-4">Please upload a CSV file with the following columns:</p>
            <v-list density="compact" class="mb-4">
              <v-list-item>
                <v-list-item-title>Column 1: Internal ID</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Column 2: Tracking Number</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Column 3: Tracking Service</v-list-item-title>
              </v-list-item>
            </v-list>
            <p class="text-caption mb-4">Note: First row will be considered as header and skipped</p>
            <v-file-input
              v-model="importFile"
              accept=".csv"
              label="Select CSV file"
              :loading="importing"
              @change="handleFileChange"
            ></v-file-input>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey"
              variant="text"
              @click="showImportDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="importing"
              :disabled="!importFile"
              @click="processImport"
            >
              Import
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, watch } from 'vue'
import ShipmentSearchForm from '@/components/sales/ShipmentSearchForm.vue'
import LateShipmentItem from '@/components/sales/LateShipmentItem.vue'
import SendEmailForm from '@/components/common/SendEmailForm.vue'
import PaginationLinks from '@/components/common/PaginationLinks.vue'
import { salesService } from '@/services/salesService'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const error = ref(null)
const sales = ref([])
const currentPage = ref(1)
const totalPages = ref(1)
const selectedSales = ref([])
const selectAll = ref(false)
const selectedSale = ref(null)
const sending = ref(false)
const ignoring = ref(false)
const totalItems = ref(0)

const showMessageDialog = ref(false)
const submitTrackingRequest = ref(false)
const to = ref('')
const subject = ref('')
const message = ref('')

const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('error')
const showImportDialog = ref(false)
const importFile = ref(null)
const importing = ref(false)

const searchParams = ref({
  ponumber: route.query.ponumber || '',
  vendor:  route.query.vendor || '',
  stores:  route.query.stores || '',
  buyerid: route .query.buyerid || '',
  shippingname: route.query.shippingname || ''
})

const fetchSales = async () => {
  loading.value = true
  error.value = null
  try {
    console.log(Object.entries(searchParams.value));
    const filters = Object.entries(searchParams.value).filter(([, value]) => !!value).map(([key, value]) => `${key}:${value}`).join(',');

    console.log('filters', filters);
    const response = await salesService.getLateShipments(currentPage.value, { filters })
    sales.value = response.data
    totalPages.value = response.last_page
    totalItems.value = response.total
  } catch (err) {
    console.error('Error fetching late shipments:', err)
    error.value = 'Failed to load late shipments'
  } finally {
    loading.value = false
  }
}

const toggleSelectAll = () => {
  if (selectAll.value) {
    selectedSales.value = sales.value.map(sale => sale.ID)
  } else {
    selectedSales.value = []
  }
}

const handleSelectionChanged = (saleId) => {
  const index = selectedSales.value.indexOf(saleId)
  if (index === -1) {
    selectedSales.value.push(saleId)
  } else {
    selectedSales.value.splice(index, 1)
  }
}



const ignoreSales = async () => {
  if (selectedSales.value.length === 0) return
  
  ignoring.value = true
  try {
    // Process all selected sales
    await Promise.all(
      selectedSales.value.map(id => 
        salesService.updateTracking(id, 'Ignored')
      )
    )
    
    // Refresh the list
    await fetchSales()
    
    // Clear selection
    selectedSales.value = []
    selectAll.value = false
  } catch (err) {
    console.error('Error ignoring sales:', err)
    error.value = 'Failed to ignore sales'
  } finally {
    ignoring.value = false
  }
}

const handleSearch = (params) => {
  searchParams.value = params
  currentPage.value = 1
  router.push({ query: searchParams.value })
  fetchSales()
}

const handleSaleUpdate = async (updatedSale) => {
  try {
    await salesService.updateSale(updatedSale.ID, updatedSale)
    const index = sales.value.findIndex(s => s.ID === updatedSale.ID)
    if (index !== -1) {
      sales.value[index] = updatedSale
    }
  } catch (err) {
    console.error('Error updating sale:', err)
  }
}

const generateMessage = (sale) => {
  return `

Order date: ${sale.CreatedDate}
Order number: ${sale.invoice_id}
PO number: ${sale.PONumber}
Items Purchased:
${sale.itemtitle}
`
}
const handleSubmitTrackingRequest = () => {
  showMessageDialog.value = true
  submitTrackingRequest.value = true
  to.value = ''
  subject.value = 'Tracking Numbers Requested'
  message.value = '';
  sales.value.filter(sale => selectedSales.value.includes(sale.ID)).forEach((sale, index) => {
    let address = `${ucwords(sale.shippingname)}
${ucwords(sale.shippingstreet1)}`;
    if(sale.shippingstreet2) {
      address += `\n${ucwords(sale.shippingstreet2)}`;
    }
    address += `\n${ucwords(sale.city)}, ${ucwords(sale.state)} ${sale.zip}`;
    message.value += `
Order # ${index + 1}

PO # ${sale.PONumber}
Item # ${sale.itemnumber ?? sale.SKU}
Quantity: ${sale.quantity}
Shipping Method: ${sale.shippingtype}
Ship To:
${address}
`
  })
}
const ucwords = function(text) {
  const str = text.toLowerCase();
  return str.replace(
    /(^([a-zA-Z\p{M}]))|([ -][a-zA-Z\p{M}])/g,
    s => s.toUpperCase()
  );
};

const openMessageDialog = (sale) => {
  selectedSale.value = sale
  showMessageDialog.value = true
  submitTrackingRequest.value = false
  to.value = ''
  subject.value = `Shipping update for your Amazon.com order ${sale.invoice_id || sale.PONumber}`
  message.value = generateMessage(sale)
}

const handleSendMessage = async (formData) => {
  sending.value = true
  snackbar.value = false
  try {
    if (submitTrackingRequest.value) {
      await salesService.submitTrackingRequest(formData)
    } else {
      await salesService.sendMessage(selectedSale.value.ID, formData)
    }
    showMessageDialog.value = false
    submitTrackingRequest.value = false
    snackbarText.value = `Successfully sent message to ${formData.to}`
    snackbarColor.value = 'success'
    snackbar.value = true
  } catch (err) {
    console.error('Error sending message:', err)
    snackbarText.value = 'Failed to send message'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    sending.value = false
  }
}

const importTracking = () => {
  showImportDialog.value = true
}

const handleFileChange = (file) => {
  if (!file) return
  
  // Validate file type
  if (!file.type && !file.name.endsWith('.csv')) {
    snackbarText.value = 'Please upload a CSV file'
    snackbarColor.value = 'error'
    snackbar.value = true
    importFile.value = null
  }
}

const processImport = async () => {
  if (!importFile.value) return
  
  importing.value = true
  try {
    const reader = new FileReader()
    
    reader.onload = async (e) => {
      const text = e.target.result
      const rows = text.split('\n')
      
      // Skip header row and filter out empty rows
      const dataRows = rows.slice(1).filter(row => row.trim())
      
      // Process sales in chunks of 10
      const chunkSize = 10
      const chunks =  []
      for (let i = 0; i < dataRows.length; i += chunkSize) {
        chunks.push(dataRows.slice(i, i + chunkSize))
      }
      
      //send each chunk to the server
      for (const chunk of chunks) {
        await Promise.all(chunk.map(row => {
          const [id, tracking, service] = row.split(',').map(field => field.trim())
          return salesService.updateTracking(id, tracking, service, 1)
        }))
      }

      // Show success message
      snackbarText.value = `Successfully updated ${dataRows.length} tracking numbers`
      snackbarColor.value = 'success'
      snackbar.value = true
      
      // Close dialog and refresh
      showImportDialog.value = false
      importFile.value = null
      fetchSales()
      
    }
    
    reader.readAsText(importFile.value)
    
  } catch (err) {
    console.error('Error importing tracking numbers:', err)
    snackbarText.value = 'Failed to import tracking numbers'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    importing.value = false
  }
}
const handlePageChange = (page) => {
  router.push({
    query: {
      ...route.query,
      page: page.toString()
    }
  })
}

// Watch for route changes to update page
watch(
  () => route.query,
  (query) => {
    const page = parseInt(query.page) || 1
    if (page !== currentPage.value) {
      currentPage.value = page
      fetchSales()
    }
  },
  { immediate: true }
)

fetchSales()
</script> 